import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Divider
} from '@mui/material';
import DynamicFilter, { FilterButton, FilterResultsCount, SelectedFiltersDisplay } from './index';

/**
 * Users Filter Modal Use Case Demonstration
 * 
 * This file demonstrates:
 * 1. Modal mode using ReusableModal
 * 2. User filtering functionality
 * 3. External filter display components
 */

// Example filter configurations
const userFilters = [
  {
    key: 'invitation_status',
    label: 'Invitation',
    type: 'checkbox',
    options: [
      { key: 'invited', label: 'Invited' },
      { key: 'not_invited', label: 'Not Invited' }
    ]
  },
  {
    key: 'sale_status',
    label: 'Sale Status',
    type: 'checkbox',
    options: [
      { key: 'active', label: 'Active' },
      { key: 'inactive', label: 'Inactive' }
    ]
  },
  {
    key: 'dsp_sales',
    label: 'DSP Sales',
    type: 'checkbox',
    options: [
      { key: 'high', label: 'High Performance' },
      { key: 'medium', label: 'Medium Performance' },
      { key: 'low', label: 'Low Performance' }
    ]
  },
  {
    key: 'kyc_status',
    label: 'KYC',
    type: 'checkbox',
    options: [
      { key: 'verified', label: 'Verified' },
      { key: 'pending', label: 'Pending' }
    ]
  },
  {
    key: 'invitation',
    label: 'Invitation',
    type: 'checkbox',
    options: [
      { key: 'invited', label: 'Invited' },
      { key: 'not_invited', label: 'Not Invited' }
    ]
  },
  {
    key: 'test_invitation',
    label: 'Test Invitation',
    type: 'checkbox',
    options: [
      { key: 'invited', label: 'Invited' },
      { key: 'not_invited', label: 'Not Invited' }
    ]
  },
];



// Sample data
const sampleUsers = [
  { id: 1, name: 'John Doe', invitation_status: 'invited', sale_status: 'active', dsp_sales: 'high', kyc_status: 'verified', date: new Date('2024-01-15') },
  { id: 2, name: 'Jane Smith', invitation_status: 'invited', sale_status: 'active', dsp_sales: 'medium', kyc_status: 'pending', date: new Date('2024-02-10') },
  { id: 3, name: 'Bob Wilson', invitation_status: 'not_invited', sale_status: 'inactive', dsp_sales: 'low', kyc_status: 'verified', date: new Date('2024-01-20') },
  { id: 4, name: 'Alice Brown', invitation_status: 'invited', sale_status: 'inactive', dsp_sales: 'medium', kyc_status: 'pending', date: new Date('2024-03-05') },
  { id: 5, name: 'Charlie Davis', invitation_status: 'not_invited', sale_status: 'active', dsp_sales: 'high', kyc_status: 'verified', date: new Date('2024-02-20') },
  { id: 6, name: 'Eva Martinez', invitation_status: 'invited', sale_status: 'active', dsp_sales: 'low', kyc_status: 'pending', date: new Date('2024-03-15') }
];

const DynamicFilterUseCase = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState(sampleUsers);
  const [appliedUserFilters, setAppliedUserFilters] = useState({});
  const [userFilterCount, setUserFilterCount] = useState(0);

  const applyFilters = (filters) => {
    setAppliedUserFilters(filters);
    
    let filtered = [...sampleUsers];

    // Apply search filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(query)
      );
    }

    // Apply user-specific filters
    if (filters.invitation_status?.length > 0) {
      filtered = filtered.filter(item => filters.invitation_status.includes(item.invitation_status));
    }
    if (filters.sale_status?.length > 0) {
      filtered = filtered.filter(item => filters.sale_status.includes(item.sale_status));
    }
    if (filters.dsp_sales?.length > 0) {
      filtered = filtered.filter(item => filters.dsp_sales.includes(item.dsp_sales));
    }
    if (filters.kyc_status?.length > 0) {
      filtered = filtered.filter(item => filters.kyc_status.includes(item.kyc_status));
    }

    // Apply date range filter
    if (filters.dateRange && (filters.dateRange.start || filters.dateRange.end)) {
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date);
        if (filters.dateRange.start && itemDate < filters.dateRange.start) return false;
        if (filters.dateRange.end && itemDate > filters.dateRange.end) return false;
        return true;
      });
    }

    // Apply sorting
    if (filters.sortBy) {
      filtered.sort((a, b) => {
        switch (filters.sortBy) {
          case 'name_asc': return a.name.localeCompare(b.name);
          case 'name_desc': return b.name.localeCompare(a.name);
          case 'date_asc': return new Date(a.date) - new Date(b.date);
          case 'date_desc': return new Date(b.date) - new Date(a.date);
          default: return 0;
        }
      });
    }

    setFilteredUsers(filtered);

    // Calculate active filter count
    let count = 0;
    if (filters.searchQuery) count++;
    if (filters.sortBy) count++;
    if (filters.dateRange && (filters.dateRange.start || filters.dateRange.end)) count++;
    
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        count += value.length;
      }
    });
    
    setUserFilterCount(count);
  };

  const clearFilters = () => {
    setFilteredUsers(sampleUsers);
    setAppliedUserFilters({});
    setUserFilterCount(0);
  };

  const removeSpecificFilter = (filterKey, optionKey) => {
    const updatedFilters = { ...appliedUserFilters };
    
    if (filterKey === 'search') {
      updatedFilters.searchQuery = '';
    } else if (filterKey === 'sort') {
      updatedFilters.sortBy = '';
    } else if (filterKey === 'dateRange') {
      updatedFilters.dateRange = { start: null, end: null };
    } else if (optionKey) {
      const current = updatedFilters[filterKey] || [];
      updatedFilters[filterKey] = current.filter(k => k !== optionKey);
    }
    
    applyFilters(updatedFilters);
  };

  const renderDataCards = (data, type) => (
    <Grid container spacing={2}>
      {data.map((item) => (
        <Grid item xs={12} sm={6} md={4} key={item.id}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {item.name}
              </Typography>
              {type === 'users' && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Invitation: {item.invitation_status}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sale Status: {item.sale_status}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    DSP Sales: {item.dsp_sales}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    KYC: {item.kyc_status}
                  </Typography>
                </>
              )}
              {type === 'products' && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Category: {item.category}
                  </Typography>
                  {item.brand && (
                    <Typography variant="body2" color="text.secondary">
                      Brand: {item.brand}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Price: ${item.price}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Availability: {item.availability}
                  </Typography>
                </>
              )}
              <Typography variant="body2" color="text.secondary">
                Date: {item.date.toLocaleDateString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>

        <Box>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <FilterButton 
              onOpenFilter={() => setModalOpen(true)}
              activeFilterCount={userFilterCount}
              label="Filter"
            />
          </Stack>

          <FilterResultsCount
            totalItems={sampleUsers.length}
            filteredItems={filteredUsers.length}
          />

          <SelectedFiltersDisplay
            appliedFilters={appliedUserFilters}
            filterConfig={userFilters}
            onRemoveFilter={(filterKey, optionKey) => 
              removeSpecificFilter(
                filterKey, 
                optionKey, 
                appliedUserFilters, 
                (filters) => applyFilters(filters, sampleUsers, setFilteredUsers, setAppliedUserFilters, setUserFilterCount, 'users')
              )
            }
            onClearAll={() => clearFilters(sampleUsers, setFilteredUsers, setAppliedUserFilters, setUserFilterCount)}
          />

          <Divider sx={{ mb: 3 }} />
          {renderDataCards(filteredUsers, 'users')}
          
          <DynamicFilter
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            title="Filter"
            filterConfig={userFilters}
            initialFilters={appliedUserFilters}
            onApply={(filters) => applyFilters(filters, sampleUsers, setFilteredUsers, setAppliedUserFilters, setUserFilterCount, 'users')}
            onClear={() => clearFilters(sampleUsers, setFilteredUsers, setAppliedUserFilters, setUserFilterCount)}
            showSearch={true}
            showDateRange={true}
            showSort={true}
          />
        </Box>
    </Container>
  );
};

export default DynamicFilterUseCase;